import { AiIcon } from '@plato/ui-legacy/assets/icons/ai-icon';
import { ProductIcon } from '@plato/ui-legacy/assets/icons/product-icon';
import { Button } from '@plato/ui-legacy/components/ui/button';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from '@plato/ui-legacy/components/ui/carousel';
import Text from '@plato/ui-legacy/components/ui/text';
import { cn } from '@plato/ui/util/classes';
import { keepPreviousData } from '@tanstack/react-query';
import { PlusIcon } from 'lucide-react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProductVariantDTO } from '../../../server/dto/product-variant-dto.ts';
import type { SalesDraftOrder } from '../../../server/types/drafts-types.ts';
import { api } from '../../../utils/api.ts';
import { ProductLinkCell } from '../../list-views/components/product-link-cell.tsx';

export type RelatedItemsProps = {
    draft: SalesDraftOrder;
    productVariantId: string;
    className?: string;
    onSelectProduct: (product: ProductVariantDTO) => void;
};

export function RelatedItems({
    draft,
    productVariantId,
    className,
    onSelectProduct,
}: RelatedItemsProps): ReactElement | null {
    const { t } = useTranslation('sales-draft');

    const { data } = api.platoUser.query.salesDraft.getInventory.getRelatedItems.useQuery(
        {
            product_variant_id: productVariantId,
            exclude_product_variant_ids: draft.line_items
                .map((item) => item.normal_line_item?.product_variant_id)
                .filter((id): id is string => !!id),
        },
        { placeholderData: keepPreviousData },
    );

    if (!data?.length) {
        return null;
    }

    return (
        <Carousel
            opts={{ align: 'start' }}
            className={cn('flex flex-col gap-2 sm:col-span-full px-3 pb-2 sm:px-2', className)}
        >
            <div className="flex items-center gap-2">
                <AiIcon />
                <Text weight="semibold" color="neutral-800">
                    {t('sales_draft__related_items_header')}
                </Text>
                <div className="ml-auto flex items-center gap-1">
                    <CarouselPrevious variant="icon" className="rounded-md" />
                    <CarouselNext variant="icon" className="rounded-md" />
                </div>
            </div>
            <CarouselContent className="-ml-3">
                {data.map((product) => (
                    <CarouselItem
                        key={product.product_variant_id}
                        className="basis-auto  pl-3 last:[&>div]:border-r"
                    >
                        <div className="flex max-w-64 items-center gap-2 pr-3 border-r">
                            <ProductIcon className="text-neutral-400 size-4" />
                            <ProductLinkCell
                                erpProductId={product.product_variant_id}
                                productName={product.product_variant_name}
                                topClassName="text-blue-400"
                                bottomClassName="text-neutral-600 line-clamp-2"
                            />
                            <div className="shrink-0">
                                <Button
                                    variant="icon"
                                    size="icon"
                                    icon={PlusIcon}
                                    onClick={() => {
                                        onSelectProduct(product);
                                    }}
                                />
                            </div>
                        </div>
                    </CarouselItem>
                ))}
            </CarouselContent>
        </Carousel>
    );
}
